<script setup lang="ts">
import {computed, ref} from "vue";
import {useWebPopup, WebForm, WebInput} from "lct-lib";
import {useCartStore} from "../cart.store";
import UploadImage from "../../design-tool/libs/UploadImage.vue";

type ProductType = "banner-5x3" | "pennant";
type BannerOption = "pole-pocket" | "hem-grommets";
type PennantOption = "hole" | "pole-pocket";
type PennantShape = "triangle" | "home-plate";

const {show} = useWebPopup();
const {addItem} = useCartStore();

const customOrderForm = ref({
  teamName: "",
  teamManager: "",
  asstManager: "",
  coach: "",
  teamParent: "",
  teamSponsor: "",
  organizationInfo: "",
  designRequest: "",
  designAssets: [] as string[],
  players: [
    {name: "", number: ""},
    {name: "", number: ""},
    {name: "", number: ""}
  ],
  productType: "banner-5x3" as ProductType,
  bannerOption: "hem-grommets" as BannerOption,
  pennantOption: "hole" as PennantOption,
  pennantShape: "triangle" as PennantShape,
  qty: 1
});

const isBanner = computed(() => customOrderForm.value.productType === "banner-5x3");
const isPennant = computed(() => customOrderForm.value.productType === "pennant");

const bannerUnitPrice = 69.99;
const pennantHolePrice = 9.99;
const pennantPolePocketPrice = 13.99;

const unitPrice = computed(() => {
  if (isBanner.value) return bannerUnitPrice;
  if (customOrderForm.value.pennantOption === "pole-pocket") return pennantPolePocketPrice;
  return pennantHolePrice;
});

const selectedProductLabel = computed(() => {
  if (isBanner.value) return "Banner 5'x3'";
  return `Pennant (${customOrderForm.value.pennantShape === "triangle" ? "Triangle" : "Home Plate"})`;
});

const selectedOptionLabel = computed(() => {
  if (isBanner.value) return customOrderForm.value.bannerOption === "pole-pocket" ? "Pole Pocket" : "Hem & Grommets";
  return customOrderForm.value.pennantOption === "pole-pocket" ? "Pole Pocket" : "Hole";
});

const orderTotalPrice = computed(() => {
  const qty = Math.max(1, Number(customOrderForm.value.qty) || 1);
  return (unitPrice.value * qty).toFixed(2);
});

const productTypeImage = computed(() => {
  if (isBanner.value) {
    return customOrderForm.value.bannerOption === "hem-grommets"
      ? "/product-type/hem-grommets.jpg"
      : "/product-type/pole-pocket.jpg";
  }

  if (customOrderForm.value.pennantShape === "home-plate") {
    return customOrderForm.value.pennantOption === "hole"
      ? "/product-type/home-plate-hole.jpg"
      : "/product-type/home-plate-pole-pocket.jpg";
  }

  return customOrderForm.value.pennantOption === "hole"
    ? "/product-type/triangle-hole.jpg"
    : "/product-type/triangle-pole-pocket.jpg";
});

const newPlayer = () => {
  customOrderForm.value.players.push({name: "", number: ""});
};

const removePlayer = (index: number) => {
  customOrderForm.value.players.splice(index, 1);
};

const onAssetUploaded = (url: string) => {
  if (!url) return;
  if (!customOrderForm.value.designAssets.includes(url)) {
    customOrderForm.value.designAssets.push(url);
  }
};

const removeAsset = (index: number) => {
  customOrderForm.value.designAssets.splice(index, 1);
};

const addCustomOrderToCart = () => {
  const designRequest = customOrderForm.value.designRequest.trim();
  if (!designRequest) {
    show({text: "Please enter a design request before adding to cart."});
    return;
  }

  const qty = Math.max(1, Number(customOrderForm.value.qty) || 1);
  customOrderForm.value.qty = qty;

  const itemTitle = isBanner.value ? "Custom 5'x3' Banner Design Order" : "Custom Pennant Design Order";
  const priceValue = unitPrice.value.toFixed(2);

  const item = {
    item_type: "order_form",
    label: itemTitle,
    alt: itemTitle,
    img_url: productTypeImage.value,
    sale_price: priceValue,
    price: priceValue,
    qty,
    type: isBanner.value ? "custom-banner" : "custom-pennant",
    information:
      "<p>Custom design request. Design service is FREE.</p><p>You are charged for printed products only.</p>",
    form_data: {
      teamName: customOrderForm.value.teamName,
      teamManager: customOrderForm.value.teamManager,
      asstManager: customOrderForm.value.asstManager,
      coach: customOrderForm.value.coach,
      teamParent: customOrderForm.value.teamParent,
      teamSponsor: customOrderForm.value.teamSponsor,
      organizationInfo: customOrderForm.value.organizationInfo,
      players: customOrderForm.value.players,
      "design request": designRequest,
      "reference assets": customOrderForm.value.designAssets.join(", "),
      product: selectedProductLabel.value,
      shape: isPennant.value ? (customOrderForm.value.pennantShape === "triangle" ? "Triangle" : "Home Plate") : "N/A",
      option: selectedOptionLabel.value,
      "unit price": `$${priceValue}`,
      "design service": "Free"
    }
  };

  addItem(item);
  show({
    text: "Your custom design order has been added to cart.",
    buttons: [
      {text: "Ok"},
      {
        text: "View Cart",
        color: "green",
        handler: () => {
          location.href = "/cart";
        }
      }
    ]
  });
};
</script>

<template>
  <div c2c-0 class="custom-design-page">
    <section c2c-1 class="hero">
      <p c2c-2 class="hero-kicker">Custom Design</p>
      <h1>Request a Fully Custom Design</h1>
      <p c2c-3 class="hero-desc">
        If none of the designs on our website match your idea, you can request a fully custom design.
        Our design service is <strong>completely free</strong>, and you only pay for the final printed product.
      </p>
    </section>

    <section c2c-4 class="custom-design-section">
      <div c2c-5 class="custom-info">
        <ul c2c-6 class="custom-pricing">
          <li>Banner 5'x3': <strong>$69.99 per banner</strong></li>
          <li>Pennant (Hole): <strong>$9.99 per pennant</strong></li>
          <li>Pennant (Pole Pocket): <strong>$13.99 per pennant</strong></li>
        </ul>
      </div>

      <aside c2c-7 class="custom-form-wrap">
        <web-form c2c-8 class="custom-form">
          <div c2c-9 class="option-block">
            <p c2c-10 class="block-label">Product Type</p>
            <div c2c-11 class="choice-row">
              <label c2c-12 class="choice-pill">
                <input v-model="customOrderForm.productType" type="radio" value="banner-5x3">
                <span>Banner 5'x3'</span>
              </label>
              <label c2c-13 class="choice-pill">
                <input v-model="customOrderForm.productType" type="radio" value="pennant">
                <span>Pennant</span>
              </label>
            </div>
          </div>

          <div c2c-14 class="option-block" v-if="isBanner">
            <p c2c-15 class="block-label">Banner Finish</p>
            <div c2c-16 class="choice-row">
              <label c2c-17 class="choice-pill">
                <input v-model="customOrderForm.bannerOption" type="radio" value="hem-grommets">
                <span>Hem &amp; Grommets</span>
              </label>
              <label c2c-18 class="choice-pill">
                <input v-model="customOrderForm.bannerOption" type="radio" value="pole-pocket">
                <span>Pole Pocket</span>
              </label>
            </div>
          </div>

          <div c2c-19 class="option-block" v-if="isPennant">
            <p c2c-20 class="block-label">Pennant Shape</p>
            <div c2c-21 class="choice-row">
              <label c2c-22 class="choice-pill">
                <input v-model="customOrderForm.pennantShape" type="radio" value="triangle">
                <span>Triangle</span>
              </label>
              <label c2c-23 class="choice-pill">
                <input v-model="customOrderForm.pennantShape" type="radio" value="home-plate">
                <span>Home Plate</span>
              </label>
            </div>
          </div>

          <div c2c-24 class="option-block" v-if="isPennant">
            <p c2c-25 class="block-label">Pennant Option</p>
            <div c2c-26 class="choice-row">
              <label c2c-27 class="choice-pill">
                <input v-model="customOrderForm.pennantOption" type="radio" value="hole">
                <span>Hole ($9.99)</span>
              </label>
              <label c2c-28 class="choice-pill">
                <input v-model="customOrderForm.pennantOption" type="radio" value="pole-pocket">
                <span>Pole Pocket ($13.99)</span>
              </label>
            </div>
          </div>

          <div c2c-29 class="type-preview">
            <img :src="productTypeImage" alt="Selected product type preview">
          </div>

          <web-input type="text" label="Team name" label-style="col" v-model="customOrderForm.teamName"></web-input>
          <web-input
            type="text"
            label="Team Manager(s)"
            label-style="col"
            v-model="customOrderForm.teamManager"
          ></web-input>
          <web-input
            type="text"
            label="Asst. Manager(s)"
            label-style="col"
            v-model="customOrderForm.asstManager"
          ></web-input>
          <web-input type="text" label="Coaches" label-style="col" v-model="customOrderForm.coach"></web-input>
          <web-input
            type="text"
            label="Team Mom/Dad/Parent"
            label-style="col"
            v-model="customOrderForm.teamParent"
          ></web-input>
          <web-input
            type="text"
            label="Team Sponsor(s)"
            label-style="col"
            v-model="customOrderForm.teamSponsor"
          ></web-input>
          <web-input
            type="text"
            label="Organization Information"
            label-style="col"
            v-model="customOrderForm.organizationInfo"
          ></web-input>

          <div c2c-30 class="order-note">
            <label for="custom-design-request">Design Request</label>
            <textarea
              id="custom-design-request"
              v-model="customOrderForm.designRequest"
              rows="4"
              placeholder="Describe your idea, colors, text content, style, and any special instructions."
            ></textarea>
          </div>

          <div c2c-31 class="asset-upload">
            <p c2c-32 class="asset-title">Reference Images / Assets</p>
            <p c2c-33 class="asset-desc">Upload sample images, logos, or files to guide your custom design request.</p>
            <div c2c-34 class="asset-upload-box">
              <upload-image :multiple="false" @uploaded="onAssetUploaded"></upload-image>
              <p c2c-35 class="asset-hint">JPG, PNG, SVG, or GIF</p>
            </div>
            <div c2c-36 class="asset-grid" v-if="customOrderForm.designAssets.length">
              <div c2c-37 class="asset-item" v-for="(assetUrl, assetIndex) in customOrderForm.designAssets" :key="assetUrl">
                <img :src="assetUrl" alt="Uploaded design asset preview">
                <button type="button" c2c-38 class="remove-asset" @click="removeAsset(assetIndex)">Remove</button>
              </div>
            </div>
          </div>

          <div c2c-39 class="player-box">
            <div c2c-40 class="player-head">
              <p>Player Name ({{ customOrderForm.players.length }})</p>
              <p>Number</p>
            </div>

            <div c2c-41 class="player-row" v-for="(player, index) in customOrderForm.players" :key="index">
              <input type="text" c2c-42 class="input-name" v-model="player.name" placeholder="The name of the player">
              <input type="text" c2c-43 class="input-number" v-model="player.number" placeholder="No.">
              <button type="button" c2c-44 class="remove-player icon-cancel" @click="removePlayer(index)"></button>
            </div>

            <button type="button" c2c-45 class="new-player" @click="newPlayer">+ New Player</button>
          </div>

          <div c2c-46 class="price-box">
            <div c2c-47 class="qty-block">
              <label for="custom-order-qty">Quantity</label>
              <input id="custom-order-qty" v-model.number="customOrderForm.qty" min="1" type="number">
            </div>

            <p c2c-48 class="price-line">
              <span>Unit Price:</span>
              <span c2c-49 class="sale-price">${{ unitPrice.toFixed(2) }}</span>
            </p>
            <p c2c-50 class="total-line">
              Estimated Total ({{ customOrderForm.qty || 1 }}): <strong>${{ orderTotalPrice }}</strong>
            </p>
            <p c2c-51 class="free-design">Custom design service: Free</p>
          </div>

          <button type="button" c2c-52 class="add-cart-btn" @click="addCustomOrderToCart">
            + Add Custom Order To Cart
          </button>
        </web-form>
      </aside>
    </section>
  </div>
</template>

<style scoped>
.custom-design-page {
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  padding: 22px 14px 44px;
  color: #0f172a;
}

.hero {
  border: 1px solid #dbeafe;
  border-radius: 18px;
  padding: 24px 20px;
  background:
    radial-gradient(120% 180% at 100% 0%, rgba(59, 130, 246, 0.14) 0%, rgba(59, 130, 246, 0) 55%),
    linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%);
}

.hero-kicker {
  margin: 0;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h1 {
  margin: 8px 0 0;
  font-size: clamp(30px, 4vw, 42px);
  line-height: 1.08;
}

.hero-desc {
  margin: 12px 0 0;
  max-width: 760px;
  color: #475569;
  line-height: 1.75;
}

.custom-design-section {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 980px;
  margin-left: auto;
  margin-right: auto;
}

.custom-info {
  border: 1px solid #dbeafe;
  border-radius: 14px;
  padding: 18px;
  background:
    radial-gradient(140% 170% at 100% 0%, rgba(14, 165, 233, 0.14) 0%, rgba(14, 165, 233, 0) 55%),
    linear-gradient(180deg, #f8fdff 0%, #eefbff 100%);
}

.custom-pricing {
  margin: 0;
  padding-left: 18px;
  color: #0f172a;
  line-height: 1.8;
}

.custom-form-wrap {
  border: 1px solid #dbeafe;
  border-radius: 14px;
  padding: 14px;
  background:
    radial-gradient(160% 180% at 100% 0%, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 58%),
    linear-gradient(180deg, #f8fbff 0%, #f0f7ff 100%);
}

.custom-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.block-label {
  margin: 0;
  color: #334155;
  font-size: 14px;
  font-weight: 800;
}

.choice-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.choice-pill {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #ffffff;
  color: #1e3a8a;
  font-weight: 700;
  padding: 8px 12px;
  cursor: pointer;
}

.choice-pill input {
  margin: 0;
}

.type-preview {
  margin-top: 2px;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
  padding: 8px;
}

.type-preview img {
  width: 100%;
  display: block;
  height: auto;
  max-height: 220px;
  object-fit: contain;
}

.order-note {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.order-note label {
  color: #334155;
  font-size: 14px;
  font-weight: 800;
}

.order-note textarea {
  min-height: 96px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  padding: 10px;
  font: inherit;
  resize: vertical;
}

.asset-upload {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.asset-title {
  margin: 0;
  color: #334155;
  font-size: 14px;
  font-weight: 800;
}

.asset-desc {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
}

.asset-upload-box {
  display: flex;
  align-items: center;
  gap: 10px;
}

.asset-hint {
  margin: 0;
  color: #64748b;
  font-size: 12px;
}

.asset-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}

.asset-item {
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #ffffff;
  padding: 8px;
}

.asset-item img {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 8px;
  display: block;
}

.remove-asset {
  margin-top: 6px;
  width: 100%;
  min-height: 30px;
  border-radius: 8px;
  border: 1px solid #fecaca;
  background: #fff1f2;
  color: #dc2626;
  font-weight: 700;
  cursor: pointer;
}

.player-head {
  display: grid;
  grid-template-columns: 1fr 90px;
  gap: 8px;
  font-weight: 800;
  font-size: 15px;
}

.player-head p {
  margin: 0 0 8px;
}

.player-row {
  display: grid;
  grid-template-columns: 1fr 90px 34px;
  gap: 8px;
  margin-bottom: 8px;
}

.input-name,
.input-number {
  min-height: 38px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  padding: 0 10px;
}

.input-number {
  text-align: center;
}

.remove-player {
  min-height: 38px;
  border-radius: 8px;
  border: 1px solid #fecaca;
  background: #fff1f2;
  color: #dc2626;
  cursor: pointer;
}

.new-player {
  margin-top: 2px;
  border: none;
  background: none;
  color: #0f766e;
  font-weight: 800;
  cursor: pointer;
  padding: 0;
}

.price-box {
  margin-top: 6px;
}

.qty-block {
  display: grid;
  grid-template-columns: 70px 1fr;
  align-items: center;
  gap: 8px;
}

.qty-block label {
  font-size: 14px;
  color: #334155;
  font-weight: 700;
}

.qty-block input {
  min-height: 38px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  padding: 0 10px;
}

.price-line {
  margin: 10px 0 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.sale-price {
  color: #dc2626;
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
}

.total-line {
  margin: 8px 0 0;
  color: #334155;
}

.free-design {
  margin: 4px 0 0;
  color: #15803d;
  font-weight: 800;
}

.add-cart-btn {
  margin-top: 10px;
  width: 100%;
  min-height: 44px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(90deg, #0b5ad9 0%, #2563eb 100%);
  color: #ffffff;
  font-weight: 800;
  cursor: pointer;
}

@media (max-width: 560px) {
  .custom-design-page {
    padding: 16px 10px 36px;
  }

  .hero {
    padding: 18px 14px;
  }

  .custom-info,
  .custom-form-wrap {
    padding: 14px 12px;
  }

  .player-row {
    grid-template-columns: 1fr 82px 34px;
  }

  .asset-upload-box {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
<style lang="scss" scoped>.block-label[c2c-10] {
                        display: block;
                 }.block-label[c2c-15] {
                        display: block;
                 }.block-label[c2c-20] {
                        display: block;
                 }.block-label[c2c-25] {
                        display: block;
                 }</style>